import { AddAPhoto, HideImage } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import {
  NeedsClusterReinstalledClusterWithOdometerPhotoDto,
  NeedsClusterReinstalledClusterWithOdometerPhotoDtoSchema
} from 'dtos'
import { Formik } from 'formik'
import { useLifecycleDocumentPreview } from '../checkIn/checkInPhotoDialogCommon'

interface ClusterWithOdometerPhotoDialogProps {
  initialValues: NeedsClusterReinstalledClusterWithOdometerPhotoDto
  isLoading: boolean
  onClose: () => void
  onDelete: () => void
  onSave: (values: NeedsClusterReinstalledClusterWithOdometerPhotoDto) => void
  open: boolean
  vehicleGuid: string // Used to preview image
}

export default function ClusterWithOdometerPhotoDialog({
  initialValues,
  isLoading,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: ClusterWithOdometerPhotoDialogProps) {
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getNeedsClusterReinstalledPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={NeedsClusterReinstalledClusterWithOdometerPhotoDtoSchema}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Speedometer After Modification Photo Details</DialogTitle>

                  <Grid item xs={12}>
                    <img
                      alt='Speedometer After Modification'
                      src={
                        values.clusterReinstallClusterWithOdometerPhotoFile
                          ? URL.createObjectURL(values.clusterReinstallClusterWithOdometerPhotoFile)
                          : serverSrc
                      }
                      style={{ width: '100%' }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {errors.clusterReinstallClusterWithOdometerPhotoFile && (
                      <Typography color='error' variant='body2'>
                        {errors.clusterReinstallClusterWithOdometerPhotoFile}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <input
                      accept='image/png, image/jpeg'
                      hidden
                      id='fileInput'
                      type='file'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          setFieldValue('clusterReinstallClusterWithOdometerPhotoFile', e.target.files[0])
                        }
                      }}
                    />

                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={<AddAPhoto />}
                      fullWidth
                      onClick={() => {
                        document.getElementById('fileInput')?.click()
                      }}
                      variant='outlined'
                    >
                      BROWSE OR TAKE PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading || !Boolean(values.documentGuid)}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (
                          window.confirm(
                            'Are you sure you would like to delete the uploaded Speedometer After Modification Photo?'
                          )
                        ) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography fontWeight='bold' variant='body1'>
                      Also use this photo for Odometer After Modification photo upload?
                    </Typography>
                    <RadioGroup
                      name='alsoUseForOdometerAfterModificationPhoto'
                      onChange={event => {
                        setFieldValue('alsoUseForOdometerAfterModificationPhoto', event.target.value === 'true')
                      }}
                      row
                      value={String(values.alsoUseForOdometerAfterModificationPhoto ?? true)}
                    >
                      <FormControlLabel
                        value='true'
                        control={
                          <Radio
                            checked={values.alsoUseForOdometerAfterModificationPhoto ?? true}
                            disabled={isSubmitting || isLoading}
                          />
                        }
                        label='Yes'
                      />
                      <FormControlLabel
                        value='false'
                        control={
                          <Radio
                            checked={values.alsoUseForOdometerAfterModificationPhoto === false}
                            disabled={isSubmitting || isLoading}
                          />
                        }
                        label='No'
                      />
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography>Please light up the following gauges and include them in this photo:</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography>
                      Odometer, Fuel Gauge, Speedometer, and if applicable, Tachometer, Oil Pressure Gauge, TPMS
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography>Please indicate which gauges are included in this photo:</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography fontWeight='bold' variant='body1'>
                      Tachometer
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <RadioGroup
                      name='clusterReinstallClusterWithOdometerIsTachometerPresent'
                      onChange={e => {
                        setFieldValue('clusterReinstallClusterWithOdometerIsTachometerPresent', e.target.value === 'true')
                      }}
                      row
                      value={values.clusterReinstallClusterWithOdometerIsTachometerPresent}
                    >
                      <FormControlLabel value='true' control={<Radio disabled={isLoading} />} label='Present' />
                      <FormControlLabel value='false' control={<Radio disabled={isLoading} />} label='N/A' />
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography fontWeight='bold' variant='body1'>
                      Oil Pressure Gauge
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <RadioGroup
                      name='clusterReinstallClusterWithOdometerIsOilPressureGaugePresent'
                      onChange={e => {
                        setFieldValue('clusterReinstallClusterWithOdometerIsOilPressureGaugePresent', e.target.value === 'true')
                      }}
                      row
                      value={values.clusterReinstallClusterWithOdometerIsOilPressureGaugePresent}
                    >
                      <FormControlLabel value='true' control={<Radio disabled={isLoading} />} label='Present' />
                      <FormControlLabel value='false' control={<Radio disabled={isLoading} />} label='N/A' />
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography fontWeight='bold' variant='body1'>
                      TPMS
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <RadioGroup
                      name='clusterReinstallClusterWithOdometerIsTPMSPresent'
                      onChange={e => {
                        setFieldValue('clusterReinstallClusterWithOdometerIsTPMSPresent', e.target.value === 'true')
                      }}
                      row
                      value={values.clusterReinstallClusterWithOdometerIsTPMSPresent}
                    >
                      <FormControlLabel value='true' control={<Radio disabled={isLoading} />} label='Present' />
                      <FormControlLabel value='false' control={<Radio disabled={isLoading} />} label='N/A' />
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Photo Previewer:**

Displays a preview of the uploaded photo.

If no photo is uploaded, nothing will be displayed.

**BROWSE OR TAKE PHOTO Button:**

Clicking this button opens the user’s camera app, allowing
them to:

Take a new photo and upload it.

Upload an existing photo from their device.

**DELETE UPLOADED PHOTO Button:**

Clicking this button will:

Clear/delete the currently uploaded photo.

Remove it from the Photo Previewer.

**Tachometer Radio Buttons:**

Radio buttons allowing the user to select whether the
Tachometer is present in the photo or marked as N/A.

**Oil Pressure Gauge Radio Buttons:**

Radio buttons allowing the user to select whether the Oil
Pressure Gauge is present in the photo or marked as N/A.

**TMPS Radio Buttons:**

Radio buttons allowing the user to select whether the TMPS
is present in the photo or marked as N/A.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not
save any changes

**SAVE Button:**

Clicking the NEXT button will:

Save the uploaded photo to the database and close the
dialog

`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='error' disabled={isSubmitting || isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <Button color='primary' disabled={isSubmitting || isLoading} type='submit' variant='contained'>
                  SAVE
                </Button>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
